import { AppThunk } from '@store/types';
import { setBatch } from '@reducers/batch';
import { setSelectedMachine, injectDemoMachine, removeDemoMachine } from '@reducers/machines';
import type { DemoTourConfig } from '@components/tours/types';
import {
  DEMO_BATCH_ID,
  DEMO_MACHINE_ID,
  DEMO_NC_FILE_ID,
  DEMO_PART_ID,
  DEMO_OPERATION_ID,
  DEMO_ORDER_ID,
  LEGACY_DEMO_BATCH_ID,
  LEGACY_DEMO_MACHINE_ID,
  DEMO_ID_THRESHOLD,
} from './constants';

// Demo machine for batch tour
const demoBatchMachine = {
  machineId: DEMO_MACHINE_ID,
  id: DEMO_MACHINE_ID,
  shortName: 'Demo Mill-02',
  fullName: 'Demo Milling Machine 02',
  machineStatus: {
    status: 'Active',
    subStatus: null,
    isConnected: true,
    productionBatchId: DEMO_BATCH_ID,
    ncFileStatus: 'Linked',
    ncFileName: 'DEMO-NC-FILE.nc',
    ncFileId: DEMO_NC_FILE_ID,
    partDetails: {
      partId: DEMO_PART_ID,
      partNumber: 'DEMO-PART-001',
      partDescription: 'Demo Part for Tour',
    },
    expectedEndOn: new Date(Date.now() + 7200000).toISOString(), // +2 hours
    targetDuration: 7200, // 2 hours in seconds
  },
  machineConnection: {
    switchedOn: true,
  },
  // Required for NoMachineColumnFilter component
  machineGroups: [],
  operations: [],
};

// Demo batch data structure matching ProductionBatchStatistics
const demoBatchData = {
  productionBatchId: DEMO_BATCH_ID,
  batchNumber: 'DEMO-BATCH-001',

  // Link information
  linkStatus: 'SingleLink',
  links: [
    {
      productionOperationId: DEMO_OPERATION_ID,
      productionOrderId: DEMO_ORDER_ID,
      productionOrderNumber: 'DEMO-PO-001',
      operationNumber: 'OP10',
      operationName: 'Demo Machining',
      partId: DEMO_PART_ID,
      partNumber: 'DEMO-PART-001',
      partDescription: 'Demo Part for Tour',
      quantityTarget: 100,
    },
  ],
  linksSummary: 'DEMO-PO-001 / OP10',

  // Quantity information
  quantityReady: 45,
  quantity: 100,
  targetQuantity: 100,

  // Status
  isInProgress: true,
  isClosed: false,

  // Machine information
  machineId: DEMO_MACHINE_ID,
  machineShortName: 'Demo Mill-02',
  machineFullName: 'Demo Milling Machine 02',

  // NC File information
  ncFileId: DEMO_NC_FILE_ID,
  ncFileName: 'DEMO-NC-FILE.nc',
  ncFileStatus: 'Linked',

  // Time tracking
  startedOn: new Date(Date.now() - 3600000).toISOString(), // Started 1 hour ago
  endedOn: null,

  // Timing statistics (in seconds)
  paidActiveTimeTotalInSeconds: 2400, // 40 minutes
  paidCycleTimeTotalInSeconds: 3200, // ~53 minutes
  paidSetupTimeInSeconds: 600, // 10 minutes
  paidTotalTimeTotalInSeconds: 3600, // 1 hour

  scheduledActiveTimeTotalInSeconds: 5400, // 1.5 hours
  scheduledCycleTimeTotalInSeconds: 6000, // ~1.67 hours
  scheduledSetupTimeInSeconds: 600, // 10 minutes
  scheduledTotalTimeTotalInSeconds: 7200, // 2 hours

  // Average times per cycle
  paidActiveTimeAverageInSeconds: 53, // ~1 minute per piece
  paidCycleTimeAverageInSeconds: 71,
  scheduledActiveTimeAverageInSeconds: 54,
  scheduledCycleTimeAverageInSeconds: 60,

  // Part information
  partId: DEMO_PART_ID,
  partNumber: 'DEMO-PART-001',
  partDescription: 'Demo Part for Tour',

  // Metadata
  createdOn: new Date(Date.now() - 86400000).toISOString(), // Created 1 day ago
  modifiedOn: new Date().toISOString(),
  createdBy: 'Demo System',
  modifiedBy: 'Demo System',
};

// Store original values to restore after cleanup
let originalBatch: any = null;
let originalSelectedMachine: any = null;
let originalMachines: any[] = [];
let batchDemoInjected = false;

/**
 * Inject demo data for the batch tour
 */
export const injectBatchDemo = (): AppThunk => (dispatch, getState) => {
  const state = getState();

  // Save original values for restoration
  originalBatch = state.batch?.batch?.data;
  originalSelectedMachine = state.machines?.selectedItem;
  originalMachines = state.machines?.machines || [];
  batchDemoInjected = true;

  // Inject demo machine into machines list
  dispatch(injectDemoMachine(demoBatchMachine));

  // Set demo machine as selected
  dispatch(setSelectedMachine(demoBatchMachine));

  // Set demo batch data
  dispatch(setBatch(demoBatchData));
};

/**
 * Cleanup demo data after batch tour ends.
 * No-op if injectBatchDemo was never called — prevents the mount-time
 * cleanup in useTourDemoData from wiping live batch/machine state.
 */
export const cleanupBatchDemo = (): AppThunk => (dispatch) => {
  if (!batchDemoInjected) {
    return; // Nothing was injected — don't touch Redux state
  }

  // Remove demo machine from machines list (both new and legacy IDs)
  dispatch(removeDemoMachine(DEMO_MACHINE_ID));
  dispatch(removeDemoMachine(LEGACY_DEMO_MACHINE_ID));

  // Restore original selected machine (or clear if none)
  const isDemoMachine = (id: number) => id === DEMO_MACHINE_ID || id === LEGACY_DEMO_MACHINE_ID || id < 0 || id >= DEMO_ID_THRESHOLD;

  if (originalSelectedMachine && !isDemoMachine(originalSelectedMachine.machineId)) {
    dispatch(setSelectedMachine(originalSelectedMachine));
  } else if (originalMachines.length > 0) {
    // If original selection was our demo machine, select first real machine (exclude demo machines)
    const firstRealMachine = originalMachines.find((m) => !isDemoMachine(m.machineId));
    if (firstRealMachine) {
      dispatch(setSelectedMachine(firstRealMachine));
    } else {
      dispatch(setSelectedMachine({}));
    }
  } else {
    dispatch(setSelectedMachine({}));
  }

  // Restore original batch (or clear if none)
  if (originalBatch && originalBatch.productionBatchId !== DEMO_BATCH_ID) {
    dispatch(setBatch(originalBatch));
  } else {
    dispatch(setBatch(null));
  }

  // Clear stored values
  originalBatch = null;
  originalSelectedMachine = null;
  originalMachines = [];
  batchDemoInjected = false;
};

// Export demo config for registry
export const demoConfig: DemoTourConfig = {
  tourId: 'batch-general',
  inject: injectBatchDemo,
  cleanup: cleanupBatchDemo,
  injectDelay: 100,
};
